import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useDFR } from '../context/DFRContext';
import { PropsData } from '../context';

import { Spinner } from 'react-bootstrap';
import PolarAreaChart from '../charts/PolarAreaChart';
import { Icon, Typography } from '@virkesborsen/ui-component-library';

import VBLeaflet from '@virkesborsen/vb-js-commons/lib/components/new/vbLeaflet';
import { swapCoords } from '@virkesborsen/vb-js-commons/lib/components/new/leafletConfig';

const ChartContainer = styled.div`
    display: flex;
    justify-content: center;

    canvas {
        width: 100% !important;
        height: 100% !important;
        max-width: 450px;
        aspect-ratio: 1 / 1;
    }
`

const BulletPointList = styled.ul`
    padding-left: 1.4em;

    li {
        padding-left: .2em;

        &::marker {
            font-size: .5em;
        }
    }
`

const BoxRadius = styled.div`
    border-radius: 16px;
`

const WindroseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        datalabels: {
            display: false,
            rotation: 11.25
        },
        legend: {
            display: false,
            position: 'right',
            labels: {
                generateLabels: function(chart) {
                    const datasets = chart.data.datasets;
                    return datasets.map((dataset, i) => ({
                        text: dataset.label,
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i
                    }));
                }
            }
        },
        // These are the hover effect datapoints and what to show
        tooltip: {
            callbacks: {
                label: function(context) {
                    const label = context.dataset.label + ': ' || '';
                    const actualValue = context.dataset?.actualData[context.dataIndex] ? context.dataset?.actualData[context.dataIndex] : 0
                    return actualValue > 0 ? label + actualValue.toFixed(2) + '%' : null
                }
            }
        }
    },
    interaction: {
        // If you turn this off the location of pointing is a bit weird and not super UX friendly
        mode: 'point'
    },
    scales: {
        r: {
            startAngle: -11.25,
            stacked: true,
            ticks: {
                z: 1,
                stepSize: 5,
                backdropColor: 'rgba(255, 255, 255, 0.5)',
                callback: function (value) {
                    return value.toFixed(0) + '%'
                },
            },
            // These are the location of the directions (N, E, S, W)
            pointLabels: {
                display: true,
                centerPointLabels: true,
                font: {
                    size: 14,
                    weight: 'bold'
                }
            },
            // In case you want lines in the grid
            //angleLines: { display: true }
        }
    },
}

export default function WindAnalysis() {
    const DFRContext = useDFR();
    const propsContext = useContext(PropsData);

    const dfrActiveProps = propsContext.props.filter(p => p.active && p.dfr)

    const [propMapRefs, setPropMapRefs] = useState({})
    dfrActiveProps.map((p) => {
        let has_added = false;
        if(!propMapRefs.hasOwnProperty(p.ref)){
            propMapRefs[p.ref] = React.createRef();
            has_added=true;
        }
        if(has_added){
            setPropMapRefs(propMapRefs);
        }
    })

    const setMapContent = (ref) => {
        const classColorMap = {
            1: "#EA2F14",
            2: "#E6521F",
            3: "#FB9E3A",
            4: "#FCEF91",
        }
        if(DFRContext.windEdgeRisks[ref] && DFRContext.windEdgeRisks[ref].error) {
            console.log('error', DFRContext.windEdgeRisks[ref].error)
        } else if(DFRContext.windEdgeRisks[ref] && !DFRContext.windEdgeRisks[ref].error && propMapRefs[ref]?.current?.state?.map) {
            let map = propMapRefs[ref].current.state.map
            
            let border = L.featureGroup([])
            propsContext.props.find(p => p.ref == ref).border.map(b => {
                let layer = L.polygon(swapCoords(b), {color: "#FFFFFF", fillOpacity: 0, interactive: false, type: 'polygon'})
                layer.addTo(border)
            })

            let geomFG = L.featureGroup([])
            DFRContext.windEdgeRisks[ref].data.forEach((data) => {

                const color = classColorMap[data.class]

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 1,
                    weight: 2,
                    type: data.geom.type.toLowerCase()
                }
                if(data.geom.type === 'Polygon') {
                    let layer = L.polygon(swapCoords(data.geom.coordinates), baseStyle)
                    layer.addTo(geomFG)
                } else if(data.geom.type == "MultiPolygon"){
                    const multiPolygon = {
                        type: "Feature",
                        geometry: data.geom
                    };
                    
                    let layer = L.geoJSON(multiPolygon, baseStyle)
                    layer.addTo(geomFG)
                }
                
            })
            propMapRefs[ref].current.setLayers(geomFG)
            propMapRefs[ref].current.setLayers(border)
            geomFG.addTo(map)
            border.addTo(map)
            map.fitBounds(geomFG.getBounds())
        } else {
            setTimeout(() => {
                setMapContent(ref);
            }, 100)
        }
    }

    useEffect(() => {
        dfrActiveProps.forEach((p) => {
            if(DFRContext.windEdgeRisks[p.ref] && propMapRefs[p.ref]){
                setMapContent(p.ref)
            }
        })
    }, [DFRContext.windEdgeRisks, propMapRefs, dfrActiveProps])

    const now = new Date()

    const fakeChartData = {labels: ['N', '', '', '', 'Ö', '', '', '', 'S', '', '', '', 'V', '', '', ''], datasets: []}
    const createChartData = data => {
        let total = {}
        let values = {}
        const labels = ['N', '', '', '', 'E', '', '', '', 'S', '', '', '', 'W', '', '', '']
        const datasets = Object.entries(data.data).map(i => {
            Object.entries(i[1]).map(v => {
                values[v[0]] = v[1]
                if(total[v[0]]) {
                    total[v[0]] = total[v[0]] + v[1]
                } else {
                    total[v[0]] = v[1]
                }
            })

            return {
                data: Object.values(total).map(t => (t/data.total)*100),
                label: i[0],
                actualData: Object.values(values).map(t => (t/data.total)*100),
                backgroundColor: DFRContext.windSpeeds.find(s => s.name == i[0]).color,
                borderWidth: 1,
                borderColor: 'rgba(50, 50, 50, 0.2)'
            }
        })

        return {
            labels: labels,
            datasets: datasets
        }
    }

    const loadingChart = (data, type, p) => {
        return <ChartContainer className='position-relative'>
            <div className='position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center text-center'>
                
                {!data || !data[type] ? <>
                    <Spinner animation="border" role="status" size="lg" />
                    <Typography variant='captionMedium' color='pebble-500'>Hämtar...</Typography>
                </> : <>
                    <div className='d-flex justify-content-center align-items-center rounded rounded-circle bg-red-scarlet-50 mb-2' style={{height: '40px', width: '40px'}}>
                        <Icon name='CrossSimple' color='red-scarlet-500' size="large" />
                    </div>
                    <Typography variant='captionMedium' color='pebble-500'>Hämtning misslyckades.</Typography>
                    <div role='button' onClick={() => DFRContext.getWind(p.ref, p.data.centroid, type)}>
                        <Typography variant='captionRegular' className='fw-bold' color='green-pine-500'>Försök igen.</Typography>
                    </div>
                </>}
            </div>
            <PolarAreaChart data={fakeChartData} options={WindroseOptions} />
        </ChartContainer>
    }

    const chartWrapper = (data, type, title, prop) => {
        return <div className='col-12 col-lg-6'>
            <BoxRadius className='bg-stone-50 p-3 mb-3'>
                <Typography variant='subheaderSmallMedium' className='text-center'>{title}</Typography>

                {data && data[type] && !data[type].error ? <ChartContainer>
                        <PolarAreaChart data={createChartData(data[type])} options={WindroseOptions} />
                </ChartContainer> : loadingChart(data, type, prop)}
            </BoxRadius>
        </div>
    }

    const fetchRiskLevelContainer = ref => {
        const riskValue = DFRContext.weatherData[ref]?.historic?.Byvind?.max || undefined
        const riskLevel = !riskValue ? undefined : riskValue > 25 ? 'high' : riskValue > 15 ? 'medium' : 'low'

        const boxSettings = {
            low: { 
                icon: 'CheckFilled',
                color: '',
                bgColor: 'success-200', 
                title: 'Låg risk', 
                text: 'Låg risk för vindskador. Förhållandena är gynnsamma och stabila. Ingen omedelbar åtgärd krävs.' 
            },
            medium: {
                icon: 'WarningFilled',
                color: '',
                bgColor: 'warning-200',
                title: 'Måttlig risk',
                text: 'Måttlig risk för vindpåverkan. Förhållandena kräver ökad uppmärksamhet, särskilt vid utsatta träd eller i nygallrade bestånd. Var beredd att justera utomhusaktiviteter vid behov.'
            },
            high: {
                icon: 'DangerFilled',
                color: '',
                bgColor: 'danger-300',
                title: 'Hög risk',
                text: 'Hög risk för vindskador. Förhållandena är farliga och instabila med stor risk för att träd faller. Ingen vistelse i skogen rekommenderas. Agera försiktigt och följ officiella varningar.'
            }
        }

        const box = boxSettings[riskLevel] ? boxSettings[riskLevel] : undefined
        
        if(!box) {
            return <BoxRadius className='d-flex flex-column justify-content-center align-items-center bg-white mt-3 h-100'>
                <Spinner animation="border" role="status" size="sm" />
                <Typography variant='labelLargeMedium' className='charcoal-500 mb-2'>Hämtar...</Typography>
            </BoxRadius>
        } else {
            return <BoxRadius className={`bg-${box.bgColor} h-100 mt-3`} style={{padding: '16px'}}>
                <div className='d-flex align-items-center mb-1'>
                    <Icon name={box.icon} color='charcoal-500' size="large" />
                    <Typography variant='bodyLargeMedium' className='charcoal-500 ms-1'>{box.title}</Typography>
                </div>
                <Typography variant='bodyMediumBaseRegular' className='charcoal-500'>{box.text}</Typography>
            </BoxRadius>
        }
    }

    const propertyData = p => {
        return <>
            <div className='row mt-3'>
                <div className='col-12 mb-2'>
                    <Typography variant="subheaderSmallMedium">Vindros över {p.designation}</Typography>
                </div>

                {chartWrapper(DFRContext.wind[p.ref], 'month', '30 dagar', p)}

                {chartWrapper(DFRContext.wind[p.ref], 'years', 'Senaste 10 år', p)}
            </div>
            <BoxRadius className='bg-stone-50 p-3'>
                <div className="row">
                    <div className="col-12 mt-3">
                        <Typography variant='subheaderMediumMedium'>Utsatta kantzoner</Typography>
                        <Typography variant='bodyMediumBaseRegular' className='pebble-500'>Kartan visar kantzoner med varierande grad av vindexponering. Färgskalan illustrerar risken för påverkan, ju mörkare färg desto större vindkänslighet.</Typography>
                            
                        <BoxRadius className='w-100 mt-2 overflow-hidden' style={{height: '450px'}}>
                            {DFRContext.windEdgeRisks[p.ref] && !DFRContext.windEdgeRisks[p.ref].error ? 
                                <VBLeaflet ref={propMapRefs[p.ref]} id={`${p.ref}_map`} baseMap='Mapbox Satelite' /> : DFRContext.windEdgeRisks[p.ref] && DFRContext.windEdgeRisks[p.ref].error ?
                                <div className="d-flex align-items-center justify-content-center w-100 h-100 flex-column">
                                    <div className='d-flex justify-content-center align-items-center rounded rounded-circle bg-red-scarlet-50 mb-2' style={{height: '40px', width: '40px'}}>
                                        <Icon name='CrossSimple' color='red-scarlet-500' size="large" />
                                    </div>
                                    <Typography variant='captionMedium' color='pebble-500'>Hämtning misslyckades.</Typography>
                                    <div role='button' onClick={() => DFRContext.getWindEdgeRisk(p.ref)}>
                                        <Typography variant='captionRegular' className='fw-bold' color='green-pine-500'>Försök igen.</Typography>
                                    </div>
                                </div> : 
                                <div className="d-flex align-items-center justify-content-center w-100 h-100 flex-column">
                                    <Spinner animation="border" role="status" size="lg" />
                                    <Typography variant='captionMedium' color='pebble-500'>Hämtar...</Typography>
                                </div>}
                        </BoxRadius>
                    </div>
                </div>
            </BoxRadius>

            <BoxRadius className='bg-stone-50 p-3 mt-3' style={{marginBottom: '48px'}}>
                <div className='row mt-3'>
                    <div className='col-12 col-lg-4 d-flex flex-column'>
                        <Typography variant='subheaderMediumMedium'>Vindöversikt - 30 dagar</Typography>
                        <Typography variant='bodyMediumBaseRegular' className='pebble-500'>Uppdaterad senast: {now.toLocaleDateString('sv-SE')}</Typography>
                        
                        <div className='d-flex flex-column justify-content-between flex-grow-1'>
                            <BoxRadius className='d-flex flex-column justify-content-center align-items-center bg-white py-3 py-lg-2 mt-3 h-100'>
                                <Icon name='WindSimple' color='charcoal-500' size="xlarge" />
                                <Typography variant='labelLargeMedium' className='pebble-500 my-2'>Högsta byvind</Typography>
                                {DFRContext.weatherData[p.ref]?.historic?.Byvind?.max ? <Typography variant='subheaderSmallMedium' className='pebble-500'>{Number(DFRContext.weatherData[p.ref].historic.Byvind.max).toFixed(1)} m/s</Typography> : <Spinner animation="border" role="status" size="sm" />}
                            </BoxRadius>

                            <BoxRadius className='d-flex flex-column justify-content-center align-items-center bg-white py-3 py-lg-2 mt-3 h-100'>
                                <Icon name='WindSimple' color='charcoal-500' size="xlarge" />
                                <Typography variant='labelLargeMedium' className='pebble-500 my-2'>Genomsnittligt vindstyrka</Typography>
                                {DFRContext.weatherData[p.ref]?.historic?.Vindhastighet?.avg ? <Typography variant='subheaderSmallMedium' className='pebble-500'>{Number(DFRContext.weatherData[p.ref].historic.Vindhastighet.avg).toFixed(1)} m/s</Typography> : <Spinner animation="border" role="status" size="sm" />}
                            </BoxRadius>

                            {fetchRiskLevelContainer(p.ref)}
                        </div>
                    </div>

                    <div className='col-12 col-lg-8 mt-3 mt-lg-0'>
                        <Typography variant='subheaderMediumMedium'>Vindklassificering</Typography>
                        <Typography variant='bodyMediumBaseRegular' className='pebble-500'>Vindhastighet i meter per sekund (m/s)</Typography>

                        <BoxRadius className='d-flex flex-column flex-grow-1 bg-white py-3 px-lg-3 mt-3'>
                            <div className='d-flex justify-content-between border-1 border-bottom border-pebble-50 p-2'>
                                <div className='col-5 pe-1'>
                                    <Typography variant='labelLargeMedium'>Vindstyrka</Typography>
                                </div>

                                <div className='col-3 pe-1'>
                                    <Typography variant='labelLargeMedium'>Kategori</Typography>
                                </div>

                                <div className='col-4'>
                                    <Typography variant='labelLargeMedium'>Beskrivning</Typography>
                                </div>
                            </div>

                            {DFRContext.windSpeeds.map(s => {
                                return <div className='d-flex align-items-center justify-content-between border-1 border-bottom border-pebble-50 p-2'>
                                    <div className='d-flex align-items-center col-5 pe-1'>
                                        <div className='rounded rounded-circle me-2' style={{backgroundColor: s.color, height: '20px', width: '20px', display: 'inline-block'}}></div>
                                        <Typography variant='labelLargeMedium'>{s.name} m/s</Typography>
                                    </div>

                                    <div className='col-3 pe-1'>
                                        <Typography variant='labelLargeMedium' className='pebble-500'>{s.category}</Typography>
                                    </div>

                                    <div className='col-4'>
                                        <Typography variant='labelLargeMedium' className='pebble-500'>{s.description}</Typography>
                                    </div>
                                </div>
                            })}
                        </BoxRadius>
                    </div>
                </div>
            </BoxRadius>
            
            
        </>
    }

    return <>
        <div className='row'>
            <div className='col-12'>
                <Typography variant="displayAndHeadline2XtraSmallMedium" className='mb-1'>Vindanalys</Typography>

                <Typography variant="introLeadMediumRegular" className='mb-2'>En vindros visar hur det vanligtvis blåser på en plats – både riktning och styrka – och för dig som skogsägare är det ett värdefullt verktyg för att förstå vindens påverkan på din skog.</Typography>

                <Typography variant="introLeadMediumRegular">En vindros hjälper dig att minska risken för skador och fatta smartare beslut som stärker din skogs långsiktiga stabilitet.</Typography>
            </div>
        </div>
        
        {dfrActiveProps.map(p => propertyData(p))}

        <BoxRadius className='bg-stone-50 p-4 mb-4'>
            <div className='row'>
                <div className='col-12'>
                    <Typography variant='subsectionHeaderSmallMedium' className='mb-3'>Vad innebär det för dig som skogsägare?</Typography>

                    <BulletPointList>
                        <li className='pebble-500'>
                            <Typography variant='bodyMediumBaseRegular'>Träden i din skog över tid “tränade” för att stå emot vanligaste vindriktningen.</Typography>
                        </li>

                        <li className='pebble-500'>
                            <Typography variant='bodyMediumBaseRegular'>När vinden däremot kommer från ovanliga riktningar - som nordost - blir det mer utsatta. </Typography>
                        </li>

                        <li className='pebble-500'>
                            <Typography variant='bodyMediumBaseRegular'>Vår analys visar att stormar från nordost historiskt har lett till ökad en frekvens av vindfällen i din region.</Typography>
                        </li>
                    </BulletPointList>

                    <Typography variant='bodyMediumBaseRegular' className='pebble-500'>Vi rekommenderar att du särskilt kontrollerar kanter mot hyggen och gallrade bestånd som vetter mot nordost. Här är träden mest sårbara. Att i förväg identifiera dessa riskzoner gör det möjligt att minimera skador vid nästa storm.</Typography>
                </div>
            </div>
        </BoxRadius>
    </>
}
